import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import moment from "moment";
import PersonIcon from "@mui/icons-material/Person";
import EmailTwoToneIcon from "@mui/icons-material/EmailTwoTone";
import LocalPhoneTwoToneIcon from "@mui/icons-material/LocalPhoneTwoTone";
import CakeTwoToneIcon from "@mui/icons-material/CakeTwoTone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { Alert, Chip, Grid, Typography } from "@mui/material";
import Client from "../../../Objects/models/Client";
import { Address } from "../../../Objects/models/Address";
import ClientRepository from "../../../Repositories/ClientRepository";
import AddressRepository from "../../../Repositories/AddressRepository";
import ImageRepository from "../../../Repositories/ImageRepository";
import Functions from "../../../Helpers/Functions";
import { capitalizeFirstLetter, formatPhoneNumber } from "../../../Utils/Function";
import { fetchImageProfile, userClientSetAccountAction } from "../../../Redux/Actions/UserActions";
import { SHOW_ERROR } from "../../../Redux/Reducers/MessageReducers";
import { AppDispatch } from "../../../Redux/store";
import Settings, { useSettingsStyle } from "../../../Components/Settings";
import SettingsSection from "../../../Components/SettingsSection";
import PhotoSection from "../../../Components/PhotoSection";
import ProfileHeaderV2 from "../../SettingsV2/ProfileHeaderV2";
import FormInput from "../../../Components/Ui/FormInput";
import FormCheckbox from "../../../Components/FormFields/FormCheckbox";
import ManualAddressFields from "../../../Components/App/Address/ManualAddressFields";
import { AddressData, AddressErrors } from "../../../Hooks/InterfaceAiciOnboarding";
import {
    addressToForm,
    apiErrorsToAddressErrors,
    computeAddressErrors,
    emptyAddressData,
    toManualAddressPayload,
} from "../../../Utils/manualAddress";
import { useTailwindBrand } from "../../../Components/shared";
import { getActiveAppBrandConfig, getActivePrestataireBrandConfig, getActivePrestationBrandConfig } from "../../../config/environment.config";

interface ClientProfileProps {
    client: Client | any;
}

/**
 * Espace client : rubrique Profil des paramètres, même structure que le
 * ProfilStep du prof (carte Settings, PhotoSection, SettingsSection avec
 * édition). Identité en lecture seule, adresse et préférences de
 * notification modifiables.
 *
 * L'adresse est saisie au format du formulaire AICI (numéro obligatoire),
 * comme à l'inscription : champs séparés, validés ici puis par le serveur,
 * qui la géocode. Plus de champ Google Places.
 */
const ClientProfile = ({ client }: ClientProfileProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const { brandConfig } = useTailwindBrand();
    // paramètres repensés (espace client v2) : en-tête compact à la place de PhotoSection
    const settingsStyle = useSettingsStyle();

    const [profileLoading, setProfileLoading] = useState(false);
    const [erroImage, setErrorImage] = useState("");
    const [load, setLoad] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const [existingAddress, setExistingAddress] = useState<Address | null>(null);
    const [addressForm, setAddressForm] = useState<AddressData>(emptyAddressData());
    const [addressErrors, setAddressErrors] = useState<AddressErrors>({});
    const [addressTouched, setAddressTouched] = useState(false);

    const [isNotify, setIsNotify] = useState<boolean>(client?.isNotify ?? true);
    const [acceptMarketing, setAcceptMarketing] = useState<boolean>(client?.acceptMarketing ?? false);

    useEffect(() => {
        const fetchAddress = async () => {
            if (client?.addresses?.length) {
                try {
                    const fullAddress = await AddressRepository.fetchAddressById(client.addresses[0].id);
                    setExistingAddress(fullAddress);
                } catch (err) {
                    console.warn(err);
                }
            }
        };
        fetchAddress();
    }, [client?.addresses]);

    const formattedAddress = existingAddress
        ? existingAddress.formattedAddress ||
        `${existingAddress.streetNumber} ${existingAddress.streetName}, ${existingAddress.city}`
        : null;

    // Photo de profil : envoi immédiat, comme côté prof, avec mise à jour de
    // l'avatar (Redux) sans recharger la page.
    const getFile = (value: File | null, error: boolean) => {
        if (error || !value) {
            setErrorImage(error ? "Mauvais format de fichier" : "");
            return;
        }
        setErrorImage("");
        const fileType = value.type.split("/");
        Functions.resizeFile(value, true, fileType[1]).then((response: any) => {
            if (URL.createObjectURL(response) && client) {
                setProfileLoading(true);
                const updatedClient: any = { ...client };
                ImageRepository.createImage(response)
                    .then((imageResponse: any) => {
                        updatedClient.profilePicture = imageResponse["@id"];
                        return ClientRepository.updateClient(updatedClient);
                    })
                    .then((updatedResponse: any) => {
                        const serializedClient = JSON.parse(JSON.stringify(updatedResponse));
                        if (updatedResponse?.profilePicture?.id) {
                            dispatch(fetchImageProfile(updatedResponse.profilePicture.id));
                        }
                        dispatch(userClientSetAccountAction(serializedClient));
                        dispatch({ type: "SET_CLIENT_INFO", payload: { client: serializedClient } });
                    })
                    .catch((uploadError: any) => {
                        console.error("Error updating client:", JSON.parse(JSON.stringify(uploadError)));
                        setErrorImage("L'envoi de la photo a échoué, veuillez réessayer.");
                    })
                    .finally(() => {
                        setProfileLoading(false);
                    });
            }
        });
    };

    const startEditing = () => {
        setAddressForm(addressToForm(existingAddress));
        setAddressErrors({});
        setAddressTouched(false);
        setIsEditing(true);
    };

    const changeAddress = (patch: Partial<AddressData>) => {
        setAddressTouched(true);
        setAddressForm((prev) => {
            const next = { ...prev, ...patch };
            // validation en direct des seuls champs modifiés
            const all = computeAddressErrors(next);
            setAddressErrors((prevErrors) => {
                const merged: AddressErrors = { ...prevErrors };
                (Object.keys(patch) as (keyof AddressData)[]).forEach((key) => {
                    const errorKey = key as keyof AddressErrors;
                    if (all[errorKey]) merged[errorKey] = all[errorKey];
                    else delete merged[errorKey];
                });
                return merged;
            });
            return next;
        });
    };

    const updateClient = async () => {
        if (!client?.id) return;
        // adresse : saisie manuelle validée avant tout envoi (numéro obligatoire)
        const mustSaveAddress = addressTouched || !existingAddress;
        if (mustSaveAddress) {
            const errs = computeAddressErrors(addressForm);
            if (Object.keys(errs).length > 0) {
                setAddressErrors(errs);
                return;
            }
        }
        setLoad(true);
        try {
            let savedAddress: Address | null = null;
            if (mustSaveAddress) {
                try {
                    savedAddress = await AddressRepository.updateManualAddressForClient(
                        client.id,
                        toManualAddressPayload(addressForm),
                    );
                } catch (addressError: any) {
                    // 400 invalid_address : le serveur liste les champs refusés
                    let body: any = null;
                    try { body = JSON.parse(addressError?.message ?? ""); } catch { /* message non JSON */ }
                    const fieldErrors = apiErrorsToAddressErrors(body?.errors);
                    setAddressErrors(Object.keys(fieldErrors).length > 0
                        ? fieldErrors
                        : { streetLabel: "L'enregistrement de l'adresse a échoué, veuillez réessayer." });
                    return;
                }
                setExistingAddress(savedAddress);
            }

            // préférences de notification : PUT du client complet, seulement si
            // elles ont changé (l'adresse a sa propre route)
            const updatedClient: any = { ...client };
            updatedClient.isNotify = isNotify;
            updatedClient.acceptMarketing = acceptMarketing;
            const prefsChanged = (client?.isNotify ?? true) !== isNotify
                || (client?.acceptMarketing ?? false) !== acceptMarketing;
            let response: any = updatedClient;
            if (prefsChanged) {
                try {
                    response = await ClientRepository.updateClient(updatedClient);
                } catch (prefsError) {
                    console.error("Error updating client preferences:", JSON.parse(JSON.stringify(prefsError)));
                    dispatch({
                        type: SHOW_ERROR,
                        payload: savedAddress
                            ? "Adresse enregistrée, mais la mise à jour des préférences a échoué. Réessayez plus tard."
                            : "La mise à jour des préférences a échoué. Réessayez plus tard.",
                    });
                    response = { ...client };
                }
            }

            const serializedClient = JSON.parse(JSON.stringify(response ?? updatedClient));
            if (savedAddress) {
                serializedClient.addresses = [JSON.parse(JSON.stringify(savedAddress))];
            }
            dispatch(userClientSetAccountAction(serializedClient));
            dispatch({ type: "SET_CLIENT_INFO", payload: { client: serializedClient } });
            setAddressTouched(false);
            setAddressErrors({});
            setIsEditing(false);
        } catch (error) {
            console.error("Error during update:", JSON.parse(JSON.stringify(error)));
        } finally {
            setLoad(false);
        }
    };

    const handleSave = () => {
        updateClient();
    };

    const handleCancel = () => {
        setAddressForm(addressToForm(existingAddress));
        setAddressErrors({});
        setAddressTouched(false);
        setIsNotify(client?.isNotify ?? true);
        setAcceptMarketing(client?.acceptMarketing ?? false);
        setIsEditing(false);
    };

    const ageDisplay = client?.birthday ? `${moment().diff(moment(client.birthday), "years")} ans` : undefined;

    const profileInfo = [
        {
            label: "Prénom",
            value: capitalizeFirstLetter(client?.firstName),
            icon: <PersonIcon className="w-5 h-5" />,
        },
        {
            label: "Nom",
            value: capitalizeFirstLetter(client?.lastName),
            icon: <PersonIcon className="w-5 h-5" />,
        },
        {
            label: "Email",
            value: client?.mail,
            icon: <EmailTwoToneIcon className="w-5 h-5" />,
        },
        {
            label: "Téléphone",
            value: formatPhoneNumber(client?.phone as string),
            icon: <LocalPhoneTwoToneIcon className="w-5 h-5" />,
        },
        {
            label: "Date de naissance",
            value: client?.birthday ? moment(client.birthday).format("DD MMMM YYYY") : "",
            icon: <CakeTwoToneIcon className="w-5 h-5" />,
        },
        {
            label: "Civilité",
            value: client?.sex === "MAN" ? "Homme" : "Femme",
            icon: <PersonIcon className="w-5 h-5" />,
        },
    ];

    return (
        <Settings title="Informations générales">
            <Grid item xs={12}>
                {settingsStyle === "v2" ? (
                    <ProfileHeaderV2 teachr={client} onImageUpload={getFile} loading={profileLoading} errorMessage={erroImage || undefined} />
                ) : (
                <PhotoSection
                    teachr={client}
                    firstName={capitalizeFirstLetter(client?.firstName)}
                    lastName={capitalizeFirstLetter(client?.lastName)}
                    age={ageDisplay}
                    email={client?.mail ?? undefined}
                    memberSinceYear={client?.creationDatetime ? moment(client.creationDatetime).format("YYYY") : undefined}
                    isEditing={isEditing}
                    onImageUpload={getFile}
                    loading={profileLoading}
                    errorMessage={erroImage || undefined}
                    disableGridItem
                />
                )}
            </Grid>
            <Grid item xs={12}>
                <SettingsSection
                    title="Mon profil"
                    subtitle="Gérez les informations de votre compte, votre adresse et vos préférences de notification."
                    isEditing={isEditing}
                    onEditClick={startEditing}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    loading={load}
                >
                    {profileInfo.map((info, index) => (
                        <Grid key={index} item xs={12} sm={12} md={12} lg={6}>
                            <FormInput
                                label={info.label}
                                type="text"
                                placeholder=""
                                value={info.value ?? ""}
                                readOnly
                                onChange={() => { }}
                                className="cursor-not-allowed"
                            />
                        </Grid>
                    ))}

                    <Grid item xs={12}>
                        {isEditing ? (
                            <div className="flex flex-col gap-3">
                                <Typography
                                    variant="subtitle2"
                                    fontWeight="700"
                                    fontFamily={brandConfig.fontFamily}
                                >
                                    Adresse postale
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    fontFamily={brandConfig.fontFamily}
                                >
                                    Le numéro de voie est obligatoire : il sert au calcul de la distance avec votre {getActivePrestataireBrandConfig()} et aux déclarations.
                                </Typography>
                                <ManualAddressFields
                                    value={addressForm}
                                    errors={addressErrors}
                                    onChange={changeAddress}
                                />
                            </div>
                        ) : formattedAddress ? (
                            <div
                                className="flex items-start gap-3 rounded-xl p-4 border"
                                style={{
                                    backgroundColor: `${brandConfig.colors?.primary ?? "#16a34a"}08`,
                                    borderColor: `${brandConfig.colors?.primary ?? "#16a34a"}30`,
                                }}
                            >
                                <LocationOnIcon
                                    sx={{
                                        color: brandConfig.colors?.primary ?? "#16a34a",
                                        mt: 0.25,
                                        flexShrink: 0,
                                    }}
                                />
                                <div>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        fontFamily={brandConfig.fontFamily}
                                        display="block"
                                    >
                                        Adresse principale
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        fontWeight="600"
                                        fontFamily={brandConfig.fontFamily}
                                    >
                                        {formattedAddress}
                                    </Typography>
                                </div>
                                {existingAddress && (
                                    <Chip
                                        label="Par défaut"
                                        size="small"
                                        sx={{
                                            ml: "auto",
                                            bgcolor: `${brandConfig.colors?.primary ?? "#16a34a"}18`,
                                            color: brandConfig.colors?.primary ?? "#16a34a",
                                            fontWeight: 600,
                                            fontFamily: brandConfig.fontFamily,
                                        }}
                                    />
                                )}
                            </div>
                        ) : (
                            <Alert severity="info" sx={{ fontFamily: brandConfig.fontFamily }}>
                                Aucune adresse renseignée. Cliquez sur "✏️Modifier" pour en ajouter une.
                            </Alert>
                        )}
                    </Grid>

                    {/* Préférences de notification, modifiables en mode édition */}
                    <Grid item xs={12}>
                        <FormCheckbox
                            label={`Être notifié à chaque fois que mon ${getActivePrestataireBrandConfig()} déclare ${getActivePrestationBrandConfig('', 'un ', 'une ')} pour moi.`}
                            checked={isNotify}
                            required={false}
                            onChange={() => { if (isEditing) setIsNotify(!isNotify); }}
                            classLabel={`${isEditing ? "" : "opacity-70"} font-semibold`}
                        />
                        <FormCheckbox
                            label={`J'accepte de recevoir des offres promotionnelles de la part de ${getActiveAppBrandConfig()}.`}
                            checked={acceptMarketing}
                            required={false}
                            onChange={() => { if (isEditing) setAcceptMarketing(!acceptMarketing); }}
                            classLabel={`${isEditing ? "" : "opacity-70"} font-semibold`}
                        />
                    </Grid>
                </SettingsSection>
            </Grid>
        </Settings>
    );
};

export default ClientProfile;
